import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth, db } from "../firebase";
import {
  collection, collectionGroup, doc, getDoc, getDocs, limit,
  onSnapshot, orderBy, query, setDoc, where
} from "firebase/firestore";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { computeRiskFromAdherence } from "../utils/adherenceUtils";

const RESOLVE_TIMEOUT_MS = 3500;
const LS_KEY_GENERIC = "lastClinicId";
const lsKeyForUid = (uid) => "lastClinicId_" + uid;

function withTimeout(promise, ms = RESOLVE_TIMEOUT_MS) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error("timeout")), ms);
    promise.then(v => { clearTimeout(t); resolve(v); })
           .catch(e => { clearTimeout(t); reject(e); });
  });
}

function Skeleton({ className }) {
  return <div className={["animate-pulse rounded-lg bg-gray-200/80", className || ""].join(" ")} />;
}

function RiskBadge({ risk }) {
  const r = (risk || "").toLowerCase();
  const map = {
    low:   "bg-emerald-50 text-emerald-700 border-emerald-200",
    medium:"bg-amber-50 text-amber-700 border-amber-200",
    high:  "bg-rose-50 text-rose-700 border-rose-200",
    "":    "bg-gray-100 text-gray-600 border-gray-200"
  };
  const label = r ? r[0].toUpperCase() + r.slice(1) : "—";
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${map[r] || map[""]}`}>
      {label}
    </span>
  );
}

function StatChip({ label, value, tone }) {
  const map = {
    gray:  "bg-gray-100 text-gray-700 border-gray-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    red:   "bg-rose-50 text-rose-700 border-rose-200",
    blue:  "bg-blue-50 text-blue-700 border-blue-200",
  };
  const cls = map[tone || "gray"];
  return (
    <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${cls}`}>
      <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-white/70 px-1 font-semibold">
        {value}
      </span>
      {label}
    </div>
  );
}

function PillButton({ children, onClick, active, tone, className }) {
  const base = "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition";
  const map = {
    gray:  active ? "bg-gray-900 text-white border-gray-900"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50",
    green: active ? "bg-emerald-600 text-white border-emerald-600"
                  : "bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50",
    amber: active ? "bg-amber-500 text-white border-amber-500"
                  : "bg-white text-amber-700 border-amber-300 hover:bg-amber-50",
    red:   active ? "bg-rose-600 text-white border-rose-600"
                  : "bg-white text-rose-700 border-rose-300 hover:bg-rose-50",
  };
  return (
    <button onClick={onClick} className={[base, map[tone || "gray"], className || ""].join(" ")}>
      {children}
    </button>
  );
}

/* -------------------- RISK HELPERS -------------------- */
function coerceRiskFromString(s) {
  const x = (s || "").toString().trim().toLowerCase();
  if (!x) return "";
  if (/(^|[^a-z])(hi|high|critical|severe)\b/.test(x)) return "high";
  if (/(^|[^a-z])(med|medium)\b/.test(x)) return "medium";
  if (/(^|[^a-z])(lo|low|normal|ok)\b/.test(x)) return "low";
  if (/\bred\b|\brose\b/.test(x)) return "high";
  if (/\byellow\b|\bamber\b|\borange\b/.test(x)) return "medium";
  if (/\bgreen\b|\bemerald\b/.test(x)) return "low";
  return "";
}
function coerceRiskFromNumber(n) {
  if (n == null || isNaN(Number(n))) return "";
  const v = Number(n);
  if (v >= 0 && v <= 3) return v >= 3 ? "high" : v >= 2 ? "medium" : "low";
  if (v >= 1 && v <= 10) return v >= 8 ? "high" : v >= 4 ? "medium" : "low";
  if (v >= 0 && v <= 100) return v >= 80 ? "low" : v >= 50 ? "medium" : "high";
  return v > 10 ? "high" : v > 3 ? "medium" : "low";
}
function tryKeys(obj, keys) {
  for (const k of keys) {
    const v = obj && obj[k];
    if (v == null) continue;
    if (typeof v === "string") {
      const r = coerceRiskFromString(v);
      if (r) return r;
    } else if (typeof v === "number") {
      const r = coerceRiskFromNumber(v);
      if (r) return r;
    } else if (typeof v === "boolean") {
      const key = k.toLowerCase();
      if (key.includes("high") && v) return "high";
      if (key.includes("medium") && v) return "medium";
      if (key.includes("low") && v) return "low";
    } else if (typeof v === "object") {
      const r = getRiskLabelOnly(v);
      if (r) return r;
    }
  }
  return "";
}
function scanObjectForRisk(obj) {
  if (!obj || typeof obj !== "object") return "";
  for (const [k, v] of Object.entries(obj)) {
    const key = k.toLowerCase();
    const likely = /(risk|severity|priority|level|status|color)$/.test(key) || /^(risk|severity|priority|level|status|color)/.test(key);
    if (!likely) continue;
    if (typeof v === "string") {
      const r = coerceRiskFromString(v);
      if (r) return r;
    } else if (typeof v === "number") {
      const r = coerceRiskFromNumber(v);
      if (r) return r;
    } else if (typeof v === "boolean") {
      if (/high/.test(key) && v) return "high";
      if (/medium/.test(key) && v) return "medium";
      if (/low/.test(key) && v) return "low";
    } else if (typeof v === "object") {
      const r = scanObjectForRisk(v);
      if (r) return r;
    }
  }
  return "";
}
function getRiskLabelOnly(p) {
  const r1 = tryKeys(p, [
    "risk","riskLevel","statusRisk","risk_label","riskLabel","riskState","risk_state",
    "todayRisk","riskToday","risk_today",
    "riskColor","statusColor","alertLevel","alert_level","severity","priority","priorityLevel","level","category",
    "riskIndex","risk_index","riskScore","risk_score","score","scoreRisk"
  ]);
  if (r1) return r1;
  if (p?.highRisk === true || p?.isHighRisk === true) return "high";
  if (p?.mediumRisk === true || p?.isMediumRisk === true) return "medium";
  if (p?.lowRisk === true || p?.isLowRisk === true) return "low";
  const nested = p?.today || p?.status || p?.stats || p?.summary || p?.daily || {};
  const r2 = tryKeys(nested, ["risk","riskLevel","status","color","priority","severity","level","label","score"]);
  if (r2) return r2;
  return scanObjectForRisk(p);
}

/* --------- Name helpers (First + Last preferred) ---------- */
function fullNameFrom(p) {
  const first =
    p?.firstName ?? p?.first_name ?? p?.given_name ?? "";
  const last  =
    p?.lastName  ?? p?.last_name  ?? p?.family_name ?? "";
  const fl = `${String(first).trim()} ${String(last).trim()}`.trim();
  if (fl) return fl;
  return p?.name || p?.displayName || p?.id || "—";
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [user, setUser] = useState(auth.currentUser || null);
  const [clinicCode, setClinicCode] = useState(null);
  const [clinicMeta, setClinicMeta] = useState(null);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  // { [patientId]: { risk, pct, taken, total } }
  const [computedRisk, setComputedRisk] = useState({});
  const [riskFilter, setRiskFilter] = useState("all");
  const [queryText, setQueryText] = useState("");
  const searchRef = useRef(null);

  useEffect(() => onAuthStateChanged(auth, (u) => setUser(u || null)), []);

  useEffect(() => {
    function handler(e) {
      const mac = /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform);
      if ((mac && e.metaKey && e.key.toLowerCase() === "k") || (!mac && e.ctrlKey && e.key.toLowerCase() === "k")) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // ===== Resolve clinic + live subscriptions =====
  useEffect(() => {
    if (!user) return;

    let unsubClinic = null;
    let unsubPatients = null;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        let resolved = null;

        // A) URL ?clinic=
        const fromUrl = searchParams.get("clinic");
        if (fromUrl) {
          resolved = (fromUrl || "").trim().toUpperCase();
          try {
            await setDoc(doc(db, "provider_index", user.uid), { clinicId: resolved, code: resolved, updatedAt: new Date() }, { merge: true });
          } catch {}
          try {
            localStorage.setItem(lsKeyForUid(user.uid), resolved);
            localStorage.setItem(LS_KEY_GENERIC, resolved);
          } catch {}
          searchParams.delete("clinic");
          setSearchParams(searchParams, { replace: true });
        }

        // B) provider_index/<uid>
        if (!resolved) {
          try {
            const idxSnap = await withTimeout(getDoc(doc(db, "provider_index", user.uid)));
            if (idxSnap.exists()) {
              const ix = idxSnap.data() || {};
              resolved = (ix.clinicId || ix.code || "").toString().trim().toUpperCase() || null;
            }
          } catch {}
        }

        // C) providers CG by uid/email
        if (!resolved) {
          try {
            const cgByUid = await withTimeout(getDocs(query(collectionGroup(db, "providers"), where("uid", "==", user.uid), limit(1))));
            if (cgByUid.docs.length) resolved = (cgByUid.docs[0].ref.path.split("/")[1] || "").toUpperCase();
          } catch {}
        }
        if (!resolved && user.email) {
          const emailLower = user.email.toLowerCase();
          try {
            const cgByLower = await withTimeout(getDocs(query(collectionGroup(db, "providers"), where("emailLower", "==", emailLower), limit(1))));
            if (cgByLower.docs.length) resolved = (cgByLower.docs[0].ref.path.split("/")[1] || "").toUpperCase();
          } catch {}
        }

        // D) localStorage
        if (!resolved) {
          try {
            resolved = (localStorage.getItem(lsKeyForUid(user.uid)) || "").toUpperCase()
                   || (localStorage.getItem(LS_KEY_GENERIC) || "").toUpperCase()
                   || null;
          } catch {}
        }

        if (!resolved) {
          if (!cancelled) {
            setLoading(false);
            setClinicCode(null);
            setClinicMeta(null);
            setPatients([]);
            navigate("/signup", { replace: true });
          }
          return;
        }

        // Persist & subscribe
        try {
          await setDoc(doc(db, "provider_index", user.uid), { clinicId: resolved, code: resolved, updatedAt: new Date() }, { merge: true });
        } catch {}
        try {
          localStorage.setItem(lsKeyForUid(user.uid), resolved);
          localStorage.setItem(LS_KEY_GENERIC, resolved);
        } catch {}

        if (cancelled) return;
        setClinicCode(resolved);

        // Clinic meta
        unsubClinic = onSnapshot(doc(db, "clinics", resolved), (snap) => {
          setClinicMeta(snap.exists() ? (snap.data() || null) : null);
        });

        // Patients list
        unsubPatients = onSnapshot(collection(db, "clinics", resolved, "patients"), (snap) => {
          const rows = snap.docs.map((d) => { const v = d.data() || {}; v.id = d.id; return v; });
          setPatients(rows);
        });

        setLoading(false);
      } catch (e) {
        console.error(e);
        setLoading(false);
        navigate("/signup", { replace: true });
      }
    })();

    return () => {
      cancelled = true;
      unsubClinic && unsubClinic();
      unsubPatients && unsubPatients();
    };
  }, [user, searchParams, setSearchParams, navigate]);

  // Compute adherence/risk for patients missing labels
  useEffect(() => {
    if (!clinicCode || patients.length === 0) return;
    (async () => {
      const next = { ...computedRisk };
      const toCompute = patients.filter(p => !getRiskLabelOnly(p));
      await Promise.all(
        toCompute.map(async (p) => {
          const res = await computeRiskFromAdherence(clinicCode, p.id, 30);
          if (res.risk) next[p.id] = res;
        })
      );
      setComputedRisk(next);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicCode, patients.map(p => p.id).join(",")]);

  const clinicLabel = useMemo(() => {
    if (!clinicMeta && !clinicCode) return "—";
    return (clinicMeta?.name) || "Unnamed";
  }, [clinicMeta, clinicCode]);

  const getRiskFinal = React.useCallback((p) => {
    const r = getRiskLabelOnly(p);
    return r || computedRisk[p.id]?.risk || "";
  }, [computedRisk]);

  const getPctFinal = React.useCallback((p) => {
    if (typeof p.adherencePct === "number") return p.adherencePct;
    const pct = computedRisk[p.id]?.pct;
    return typeof pct === "number" ? pct : null;
  }, [computedRisk]);

  const counts = useMemo(() => {
    let total = patients.length, low = 0, medium = 0, high = 0;
    for (const p of patients) {
      const r = getRiskFinal(p);
      if (r === "low") low++;
      else if (r === "medium") medium++;
      else if (r === "high") high++;
    }
    return { total, low, medium, high };
  }, [patients, computedRisk, getRiskFinal]);

  const filteredPatients = useMemo(() => {
    return patients.filter((p) => {
      const r = getRiskFinal(p);
      if (riskFilter !== "all" && r !== riskFilter) return false;

      // 🔎 search includes First + Last + other fallbacks
      const hay = (
        (p?.firstName || p?.first_name || "") + " " +
        (p?.lastName  || p?.last_name  || "") + " " +
        (p?.name || "") + " " +
        (p?.displayName || "") + " " +
        (p?.mrn || "") + " " +
        (p?.id || "")
      ).toLowerCase();

      return hay.indexOf(queryText.toLowerCase()) >= 0;
    });
  }, [patients, riskFilter, queryText, computedRisk, getRiskFinal]);

  function openPatient(p) {
    navigate(`/patient/${encodeURIComponent(p.id)}?clinic=${encodeURIComponent(clinicCode || "")}`);
  }

  async function logout() {
    try { await signOut(auth); } catch {}
    navigate("/login", { replace: true });
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50/70 via-white to-white">
      <div className="sticky top-0 z-10 bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-500 text-white shadow">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3">
          <div className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-white/90" />
            <h1 className="text-sm font-semibold tracking-wide sm:text-base">
              iMedicate Clinic • <span className="opacity-90">{clinicLabel}</span>
              {clinicCode ? <span className="ml-2 text-xs opacity-80">({clinicCode})</span> : null}
            </h1>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="opacity-90">{auth.currentUser && auth.currentUser.email}</span>
            <button onClick={logout} className="rounded-full border border-white/60 bg-white/10 px-3 py-1 text-[11px] font-semibold text-white hover:bg-white/20">
              Logout
            </button>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-7xl px-5 py-6">
        <div className="mb-4 grid gap-3 md:grid-cols-3">
          <div className="rounded-xl border bg-white p-3 md:col-span-2">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="font-semibold text-gray-800">Overview</div>
              <div className="text-xs text-gray-500">
                {loading ? "Loading…" : `${filteredPatients.length} Patients`}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <StatChip label="Patients" value={counts.total} tone="blue" />
              <StatChip label="Low"     value={counts.low}   tone="green" />
              <StatChip label="Medium"  value={counts.medium} tone="amber" />
              <StatChip label="High"    value={counts.high}  tone="red" />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <div className="relative grow">
                <input
                  ref={searchRef}
                  type="search"
                  value={queryText}
                  onChange={(e) => setQueryText(e.target.value)}
                  placeholder="Search by name, MRN, or ID…"
                  className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm outline-none ring-indigo-200/70 placeholder:text-gray-400 focus:ring-4"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">⌘K</span>
              </div>
              <PillButton tone="gray"  active={riskFilter === "all"}    onClick={() => setRiskFilter("all")}>All Risks</PillButton>
              <PillButton tone="green" active={riskFilter === "low"}    onClick={() => setRiskFilter("low")}>Low</PillButton>
              <PillButton tone="amber" active={riskFilter === "medium"} onClick={() => setRiskFilter("medium")}>Medium</PillButton>
              <PillButton tone="red"   active={riskFilter === "high"}   onClick={() => setRiskFilter("high")}>High</PillButton>
              <PillButton tone="gray" className="ml-auto" onClick={() => window.location.reload()}>Reload</PillButton>
            </div>
          </div>

          <div className="rounded-xl border bg-white p-3">
            <div className="mb-2 text-xs uppercase tracking-wide text-gray-500">Clinic</div>
            {loading ? (
              <>
                <Skeleton className="mb-2 h-4 w-36" />
                <Skeleton className="h-6 w-24" />
              </>
            ) : (
              <>
                <div className="mb-1 text-sm font-medium text-gray-900">{clinicLabel}</div>
                <div className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700">
                  Code: <span className="font-mono">{clinicCode || "—"}</span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="rounded-xl border bg-white p-4">
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : filteredPatients.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <div className="rounded-2xl border border-dashed border-gray-300 p-8">
                <div className="mb-2 text-lg font-semibold text-gray-800">No patients match your filters</div>
                <p className="max-w-sm text-sm text-gray-500">Try clearing the search or switching the risk filter.</p>
              </div>
            </div>
          ) : (
            <ul className="divide-y">
              {filteredPatients.map((p) => {
                const r = getRiskFinal(p);
                const pct = getPctFinal(p);
                return (
                  <li key={p.id} className="flex items-center justify-between py-3">
                    <button onClick={() => openPatient(p)} className="text-left">
                      <div className="font-medium text-gray-900">
                        {fullNameFrom(p)}
                      </div>
                      <div className="mt-0.5 text-xs text-gray-500 flex items-center gap-2">
                        <span>{p.mrn || "—"}</span>
                        <span className="opacity-40">•</span>
                        <RiskBadge risk={r} />
                        {typeof pct === "number" ? (
                          <span className="rounded-full border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[11px] font-medium text-gray-700">
                            {pct}% adherence
                          </span>
                        ) : null}
                      </div>
                    </button>
                    <button
                      onClick={() => openPatient(p)}
                      className="rounded-full border border-gray-300 px-3 py-1 text-[11px] font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      Open
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}
